import React, { PureComponent, memo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Popover } from 'react-tiny-popover';

import FieldTableCustomColumnIds from '../../../../constants/FieldTableCustomColumnIds';
import DecoratorIconOptions from '../../../../constants/DecoratorIconOptions';
import FieldModes from '../../../../constants/FieldModes';
import FieldTypes from '../../../../constants/FieldTypes';
import { filterHiddenTableColumnsSelector } from '../../../../lib/tableHelper';
import { isFieldHiddenInView } from '../../../../lib/documentHelper';
import ThemeContext from '../../../../theme/ThemeContext';
import ThemeIcons from '../../../../theme/Icons';
import Icon from '../../../misc/Icon';
import DecoratorConfigContext from '../../../DecoratorConfigContext';

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StyledTH = styled.div`
  display: flex;
  box-sizing: border-box;
  font-size: ${(props) => props.fontSize}px;
  color: ${(props) => props.fontColor};
  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};
  border-top: ${(props) => (props.borderTop ? props.borderTop : 'transparent')};
  border-right: ${(props) => (props.borderRight ? props.borderRight : 'transparent')};
  border-left: ${(props) => (props.borderLeft ? props.borderLeft : 'transparent')};
  border-bottom: ${(props) => (props.borderBottom ? props.borderBottom : 'transparent')};
  padding: ${(props) => props.padding}px;
  border-bottom: ${(props) => props.border};
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => {
    const tl = props.borderTopLeftRadius != null ? `${props.borderTopLeftRadius}px` : '0';
    const tr = props.borderTopRightRadius != null ? `${props.borderTopRightRadius}px` : '0';
    return `${tl} ${tr} 0 0`;
  }};
  resize: none;
  ${(props) => {
    if (props.width) {
      return `max-width: ${props.width}px; min-width: ${props.width}px;`;
    } else if (props.textOverflow === 'ellipsis') {
      return 'flex: 1; min-width: 20px;';
    } else {
      return 'flex: 1;';
    }
  }}
  vertical-align: top;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  svg {
    font-size: 12px;
  }
`;

const StyledHeaderContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  min-width: 0;
  gap: 6px;
`;

const StyledTitleWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  min-width: 0;
  overflow: hidden;
`;

const StyledTitleWrapperCenter = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledTitle = styled.span`
  flex: 1;
  min-width: 0;
  ${(props) => props.textOverflow === 'ellipsis'
    ? 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'
    : 'overflow-wrap: break-word; white-space: normal;'}
`;

const StyledDecoratorInline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 50%;
  flex-shrink: 0;
  min-width: 0;
  white-space: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledDecoratorPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid ${(props) => props.$color};
  background: none;
  cursor: default;
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.$color};
  max-width: 100px;
  min-width: 0;
  flex-shrink: 0;
  line-height: 1.4;

  &:focus {
    outline: none;
  }

  svg {
    font-size: 10px;
    flex-shrink: 0;
  }
`;

const StyledDecoratorPillLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const StyledKebabTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: default;
  background: none;
  border: none;
  padding: 3px 4px;
  border-radius: 3px;
  opacity: 0.55;

  &:focus {
    outline: none;
  }

  svg {
    font-size: 12px;
  }
`;

const StyledMenu = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.$bg};
  border-radius: ${(props) => props.$radius}px;
  border: 1px solid ${(props) => props.$border};
  box-shadow: 0px 4px 20px rgba(20, 20, 23, 0.18);
  min-width: 160px;
  max-width: 240px;
  overflow: hidden;
  font-family: ${(props) => props.$fontFamily};
`;

const StyledMenuRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;

  & + & {
    border-top: 1px solid ${(props) => props.$border};
  }
`;

const StyledMenuIconWrapper = styled.div`
  flex-shrink: 0;
  width: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 12px;
  }
`;

const StyledMenuLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.$color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
`;

const getIconDefinition = (iconValue) => {
  if (!iconValue) return null;
  const match = DecoratorIconOptions.find((opt) => opt.value === iconValue);
  return match ? match.icon : ThemeIcons.decorator.image;
};

/**
 * Functional component for rendering column decorators inline with header.
 * Handles popover state for kebab menu when multiple decorators exist.
 */
const ColumnDecorators = ({ decorators, primaryColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useContext(ThemeContext);
  const { visibleLimitInFields } = useContext(DecoratorConfigContext);

  if (!decorators || !Array.isArray(decorators) || decorators.length === 0) return null;

  const validDecorators = decorators.filter((d) => d.icon || d.label);
  if (validDecorators.length === 0) return null;

  if (validDecorators.length <= visibleLimitInFields) {
    return (
      <StyledDecoratorInline ref={(el) => {
        if (el && !el.__wheelBound) { el.__wheelBound = true; el.addEventListener('wheel', function(e) { if (el.scrollWidth > el.clientWidth) { e.preventDefault(); el.scrollLeft += e.deltaY || e.deltaX; } }, {passive: false}); }
      }}>
        {validDecorators.map((decorator, index) => {
          const iconDef = getIconDefinition(decorator.icon);
          const color = decorator.color || primaryColor;
          return (
            <StyledDecoratorPill key={decorator._id || index} type="button" $color={color}>
              {iconDef ? <Icon icon={iconDef} color={color} /> : null}
              {decorator.label ? <StyledDecoratorPillLabel>{decorator.label}</StyledDecoratorPillLabel> : null}
            </StyledDecoratorPill>
          );
        })}
      </StyledDecoratorInline>
    );
  }

  const menuContent = (
    <StyledMenu
      $bg={theme.backgroundColor}
      $radius={theme.borderRadius}
      $border={theme.borderColor}
      $fontFamily={theme.fontFamily}
    >
      {validDecorators.map((decorator, index) => {
        const iconDef = getIconDefinition(decorator.icon);
        const color = decorator.color || primaryColor;
        return (
          <StyledMenuRow
            key={decorator._id || index}
            $border={theme.borderColor}
          >
            {iconDef ? (
              <StyledMenuIconWrapper>
                <Icon icon={iconDef} color={color} />
              </StyledMenuIconWrapper>
            ) : null}
            {decorator.label ? (
              <StyledMenuLabel $color={color}>{decorator.label}</StyledMenuLabel>
            ) : null}
          </StyledMenuRow>
        );
      })}
    </StyledMenu>
  );

  return (
    <Popover
      isOpen={isOpen}
      positions={['bottom', 'top', 'right', 'left']}
      reposition={true}
      containerStyle={{ zIndex: '2147483647' }}
      onClickOutside={() => setIsOpen(false)}
      content={menuContent}
    >
      <StyledKebabTrigger
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <Icon icon={ThemeIcons.misc.decorators} />
      </StyledKebabTrigger>
    </Popover>
  );
};

class TableHeader extends PureComponent {

  static contextType = ThemeContext;

  static propTypes = {

    id: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldType: PropTypes.string,

    enableNumber: PropTypes.bool,
    enableMenu: PropTypes.bool,
    enableRowDecorators: PropTypes.bool,
    decoratorColumnWidth: PropTypes.number,

    columns: PropTypes.array,
    columnWidths: PropTypes.object,

    //Action handlers
    onColumnRef: PropTypes.func,

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    textOverflow: PropTypes.string,

    loadCompletedId: PropTypes.string,
    onLoadCompleted: PropTypes.func,

  }

  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidUpdate() {

    const { onLoadCompleted, loadCompletedId } = this.props;
    const { rowRef } = this.state;

    if (onLoadCompleted && rowRef) onLoadCompleted(loadCompletedId || 'header', { height: rowRef.offsetHeight });

  }

  render() {

    const { rowRef } = this.state;

    const {
      view,
      id,
      fieldType,
      mode,
      disabled,
      columns,
      fieldPositionColumns,
      columnWidths = {},

      enableNumber,
      enableMenu,
      enableRowDecorators,
      decoratorColumnWidth,

      //Action handlers
      onColumnRef,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding,
      textOverflow,
    } = this.props;

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const utilityColumns = [];
    if (enableNumber) utilityColumns.push({ _id: FieldTableCustomColumnIds.rowNumber, type: FieldTableCustomColumnIds.rowNumber, width: 46 });
    if (enableMenu) utilityColumns.push({ _id: FieldTableCustomColumnIds.rowMenu, type: FieldTableCustomColumnIds.rowMenu, width: 20 });
    if (enableRowDecorators) utilityColumns.push({ _id: FieldTableCustomColumnIds.rowDecorators, type: FieldTableCustomColumnIds.rowDecorators, width: decoratorColumnWidth || 40 });

    const formattedColumns = [...utilityColumns, ...columns];
    const filteredColumns = filterHiddenTableColumnsSelector({columns: formattedColumns, fieldPositionColumns, view, mode: this.props.fieldMode, docFields: this.props.docFields});

    const columnNodes = filteredColumns.map((column, columnIndex) => {

      /**
       * Handle cell borders for collection vs table field type
       */
      const borders = { borderRight: border, borderBottom: border };
      if (fieldType === FieldTypes.collection && columnIndex === 0) borders.borderLeft = border;
      if (fieldType === FieldTypes.collection) borders.borderTop = border;

      const isFirst = columnIndex === 0;
      const isLast = columnIndex === filteredColumns.length - 1;
      const radiusProps = {};
      if (borderRadius != null) {
        if (isFirst) radiusProps.borderTopLeftRadius = borderRadius;
        if (isLast) radiusProps.borderTopRightRadius = borderRadius;
      }

      return (
        <StyledTH
          key={column._id}
          data-testid={`${id}-${column._id}-${columnIndex}-column-th`}
          ref={(e) => {

            if (
              e
              && onColumnRef
              && e.offsetWidth
              && column._id !== FieldTableCustomColumnIds.rowNumber
              && column._id !== FieldTableCustomColumnIds.rowMenu
              && column._id !== FieldTableCustomColumnIds.rowDecorators
            ) onColumnRef(column._id, e);

          }}
          {...{
            width: column.width,
            textOverflow,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign: column._id === FieldTableCustomColumnIds.rowDecorators ? 'center' : textAlign,
            textTransform,
            textDecoration,
            backgroundColor,
            padding,
            ...borders,
            ...radiusProps
          }}
        >
          {column._id === FieldTableCustomColumnIds.rowDecorators ? (
            <StyledTitleWrapperCenter>
              <Icon icon={ThemeIcons.misc.decorators} />
            </StyledTitleWrapperCenter>
          ) : (
            <StyledHeaderContent>
              <StyledTitleWrapper>
                {this.props.fieldMode === FieldModes.edit && (column.hidden || isFieldHiddenInView(column.hiddenViews, view) || column.logic?.conditions?.length > 0) ? (
                  <Icon icon={ThemeIcons.settings.hide} color={this.context.warningColor} size="sm" />
                ) : null}
                <StyledTitle textOverflow={textOverflow}>{column.title}</StyledTitle>
              </StyledTitleWrapper>
              {this.props.fieldMode === FieldModes.edit ? (
                <ColumnDecorators
                  decorators={column.decorators}
                  primaryColor={this.context.primaryColor}
                />
              ) : null}
            </StyledHeaderContent>
          )}
        </StyledTH>
      );
    });

    return (
      <StyledRow
        data-testid={`${id}-table-field-header-row`}
        ref={(e) => {
          if (!rowRef && e) this.setState({ rowRef: e });
        }}
        {...{
          border,
          backgroundColor
        }}
      >
        {columnNodes}
      </StyledRow>
    );

  }

}

export default memo(TableHeader);
